import React from "react";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { formatDate } from "../../shared/utilities";
import { FormControlLabel, Switch, Button } from "../../shared/components";
import { DataGrid } from "../../shared/components/DataGrid";
import { usePopoutWindowContext } from "../../shared/components/PopoutWindow";
import { useAppSelector, useThunkDispatch } from "../../store";
import { setTracingEnabled, clearTracing } from "../../store/tracing/actions";
import { traceKindLabel, traceSourceLabel } from "./traceFormat";
import CodeBlock from "./CodeBlock";
import { CodeHoverLayer, type CodeHoverApi } from "./CodeHover";

type TraceRow = {
    id: number;
    timestamp: Date;
    kind: string;
    name: string;
    source: string;
    oldValue?: string;
    newValue?: string;
};

const stringifyValue = (value: unknown) => (value === undefined ? undefined : JSON.stringify(value, null, 2));

// Old/New Value columns render a one-line syntax-highlighted chip; the full formatted value shows in
// the shared cursor-following hover tooltip (same pattern as the Profiling aggregate view), or can be
// pinned open (scrollable, selectable) via the chip's pin button.
const valueColumn = (field: "oldValue" | "newValue", headerName: string, hoverRef: React.RefObject<CodeHoverApi>): GridColDef<TraceRow> => ({
    field,
    headerName,
    sortable: false,
    flex: 1,
    minWidth: 220,
    cellClassName: "flush",
    renderCell: params =>
        params.value === undefined ? null : (
            <span
                className="trace-value-cell"
                onMouseMove={e => hoverRef.current?.move(params.value, e.clientX, e.clientY)}
                onMouseLeave={() => hoverRef.current?.hide()}
            >
                <CodeBlock code={params.value} language="json" singleLine pinnable onPinToggle={() => hoverRef.current?.hide()} />
            </span>
        ),
});

function Traces() {
    const dispatch = useThunkDispatch();
    const enabled = useAppSelector(s => s.tracing.enabled);
    const traces = useAppSelector(s => s.tracing.traces);
    // Portal the hover tooltip into the pop-out window's body so it renders in the correct window.
    const container = usePopoutWindowContext()?.window?.document.body ?? undefined;
    const hoverRef = React.useRef<CodeHoverApi>(null);

    const rows = React.useMemo<TraceRow[]>(
        () =>
            traces.map((trace, index) => {
                const { event } = trace;
                return {
                    id: index,
                    timestamp: trace.timestamp,
                    kind: traceKindLabel(event.kind),
                    name: event["name"] ?? "",
                    source: traceSourceLabel(event.source),
                    oldValue: stringifyValue(event["oldValue"]),
                    newValue: stringifyValue(event["newValue"] ?? event["error"]),
                };
            }),
        [traces]
    );

    const columns = React.useMemo<GridColDef<TraceRow>[]>(
        () => [
            { field: "timestamp", headerName: "Time", width: 110, valueFormatter: (value: Date) => formatDate(value, "HH:mm:ss.SSS") },
            { field: "kind", headerName: "Event Type", width: 150 },
            { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
            { field: "source", headerName: "Source", flex: 1, minWidth: 180 },
            valueColumn("oldValue", "Old Value", hoverRef),
            valueColumn("newValue", "New Value", hoverRef),
        ],
        []
    );

    return (
        <div className="traces-layout">
            <div className="traces-toolbar">
                <FormControlLabel
                    control={<Switch checked={enabled} onChange={(_e, v) => dispatch(setTracingEnabled(v))} />}
                    label="Enable tracing"
                />
                <span className="toolbar-spacer" />
                <span className="trace-count">{traces.length} event{traces.length === 1 ? "" : "s"}</span>
                <Button size="small" variant="contained" color="secondary" disabled={traces.length === 0} onClick={() => dispatch(clearTracing())}>Clear</Button>
            </div>

            <DataGrid
                className="traces-grid"
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                disableColumnMenu
                density="compact"
                rowHeight={36}
            />

            <CodeHoverLayer ref={hoverRef} container={container} language="json" />
        </div>
    );
}

export default React.memo(Traces);
